import { getAccountRangeAndPostfix } from '../store/utils/account-numbers'
import type {
  Account,
  CapitalisedLocale,
  Locale,
  Group,
  RawGroupLeaf,
  ErrorMessagePerLocale,
  LocalisedNamesOrOriginalNames,
} from './../shared-types'

import { useBannerManager } from '@app/shared/pinia/banner-manager'
import { bannerVariants } from '@app/shared/types/banner'

export const capitaliseLocale = (locale: Locale): CapitalisedLocale => {
  return (locale.charAt(0).toUpperCase() + locale.slice(1)) as CapitalisedLocale
}

type PickAccountNameByLocaleOptions = {
  account: Account
  noNamePrefix: boolean
  supportedLocales: CapitalisedLocale[]
}

export const firstAvailableLocalisedName = ({
  item,
  nameKey,
  supportedLocales,
}: {
  item: Account | RawGroupLeaf | Group
  nameKey: string
  supportedLocales: CapitalisedLocale[]
}): string => {
  for (const locale of supportedLocales) {
    const key = `${nameKey}${locale}` as keyof LocalisedNamesOrOriginalNames
    const value = item[key]

    if (value) {
      return value
    }
  }

  return ''
}

export const pickAccountNameByLocale = ({
  account,
  noNamePrefix,
  supportedLocales,
}: PickAccountNameByLocaleOptions): string => {
  const nameKey = noNamePrefix ? 'name' : 'originalName'

  return firstAvailableLocalisedName({
    item: account,
    nameKey,
    supportedLocales,
  })
}

export const pickGroupLeafNameByFirstAvailableLocale = (
  leaf: RawGroupLeaf | Group,
  supportedLocales: CapitalisedLocale[]
): string => {
  return firstAvailableLocalisedName({
    item: leaf,
    nameKey: 'name',
    supportedLocales,
  })
}

export const sortByMappedSuffix = (a: Account, b: Account): number => {
  const suffixA = convertSuffixToNumber(a.mappedNumber)
  const suffixB = convertSuffixToNumber(b.mappedNumber)

  if (suffixA === suffixB) {
    return 0
  }

  return suffixA > suffixB ? 1 : -1
}

export const localisedErrorMessage = (
  errorMessage: string,
  errorMessagePerLocale: ErrorMessagePerLocale = {
    en: '',
    fr: '',
    nl: '',
  }
): string => {
  const locale = window.I18n.locale as keyof ErrorMessagePerLocale
  const localeMessage = errorMessagePerLocale[locale]
  return (
    localeMessage ||
    errorMessage ||
    window.I18n.t('account_mapping_list.recommendations.error.message')
  )
}

export const showErrorBanner = (
  errorMessage: string,
  errorMessagePerLocale: ErrorMessagePerLocale | undefined
): void => {
  const bannerStore = useBannerManager()
  const message = localisedErrorMessage(errorMessage, errorMessagePerLocale)

  bannerStore.raiseBanner({
    id: 'data.error_message',
    variant: bannerVariants.danger,
    title: window.I18n.t('account_mapping_list.recommendations.error.title'),
    description: message,
  })
}

const convertSuffixToNumber = (mappedNumber: string): number => {
  const [, effectiveSuffix] = getAccountRangeAndPostfix(mappedNumber)

  return effectiveSuffix ? parseInt(effectiveSuffix) : 0
}
